import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { AppShell } from './components/AppShell';
import { Spinner } from './components/ui';
import { LoginPage, ForgotPasswordPage, OtpPage, AcceptInvitePage } from './pages/auth/Auth';
import { RegisterPage } from './pages/auth/Register';

const Buyer = {
  Dashboard: lazy(() => import('./pages/buyer/Dashboard')),
  Catalog: lazy(() => import('./pages/buyer/Catalog')),
  Product: lazy(() => import('./pages/buyer/Product')),
  Suppliers: lazy(() => import('./pages/buyer/Suppliers')),
  Supplier: lazy(() => import('./pages/buyer/Supplier')),
  Cart: lazy(() => import('./pages/buyer/Cart')),
  NewRfq: lazy(() => import('./pages/buyer/NewRfq')),
};
const SupplierPages = {
  Dashboard: lazy(() => import('./pages/supplier/Dashboard')),
  Products: lazy(() => import('./pages/supplier/Products')),
  ProductEditor: lazy(() => import('./pages/supplier/ProductEditor')),
  Finance: lazy(() => import('./pages/supplier/Finance')),
};
const Shared = {
  Orders: lazy(() => import('./pages/shared/Orders')),
  Order: lazy(() => import('./pages/shared/Order')),
  Rfqs: lazy(() => import('./pages/shared/Rfqs')),
  Rfq: lazy(() => import('./pages/shared/Rfq')),
  Invoices: lazy(() => import('./pages/shared/Invoices')),
  Invoice: lazy(() => import('./pages/shared/Invoice')),
  Disputes: lazy(() => import('./pages/shared/Disputes')),
  Dispute: lazy(() => import('./pages/shared/Dispute')),
  Support: lazy(() => import('./pages/shared/Support')),
  Ticket: lazy(() => import('./pages/shared/Ticket')),
  Settings: lazy(() => import('./pages/shared/Settings')),
  Company: lazy(() => import('./pages/shared/Company')),
  Notifications: lazy(() => import('./pages/shared/Notifications')),
  Onboarding: lazy(() => import('./pages/shared/Onboarding')),
};
const Admin = {
  Dashboard: lazy(() => import('./pages/admin/Dashboard')),
  Kyc: lazy(() => import('./pages/admin/Kyc')),
  KycDetail: lazy(() => import('./pages/admin/KycDetail')),
  Organizations: lazy(() => import('./pages/admin/Organizations')),
  Organization: lazy(() => import('./pages/admin/Organization')),
  Moderation: lazy(() => import('./pages/admin/Moderation')),
  Categories: lazy(() => import('./pages/admin/Categories')),
  Users: lazy(() => import('./pages/admin/Users')),
  Payments: lazy(() => import('./pages/admin/Payments')),
  Settlements: lazy(() => import('./pages/admin/Settlements')),
  Commissions: lazy(() => import('./pages/admin/Commissions')),
  Content: lazy(() => import('./pages/admin/Content')),
  Audit: lazy(() => import('./pages/admin/Audit')),
  Configuration: lazy(() => import('./pages/admin/Configuration')),
  Integrations: lazy(() => import('./pages/admin/Integrations')),
};

function RequireSession({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Spinner />;
  if (!me?.authenticated) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (me.pendingOtp) return <Navigate to="/verify" replace />;
  if (!me.org) {
    return (
      <div className="mx-auto max-w-md p-10 text-center">
        <p className="font-medium">Your account is not active in any organisation.</p>
        <p className="mt-1 text-ink-3">Ask an administrator of your company to invite you again.</p>
      </div>
    );
  }
  return <>{children}</>;
}

function Home() {
  const { me } = useAuth();
  const org = me!.org!;
  if (org.type === 'PLATFORM') return <Admin.Dashboard />;
  if (org.status !== 'ACTIVE') return <Navigate to="/onboarding" replace />;
  return org.type === 'BUYER' ? <Buyer.Dashboard /> : <SupplierPages.Dashboard />;
}

function ForType({ type, children }: { type: 'BUYER' | 'SUPPLIER' | 'PLATFORM'; children: ReactNode }) {
  const { me } = useAuth();
  if (me?.org?.type !== type) return <Navigate to="/" replace />;
  return <>{children}</>;
}

const RedirectParam = ({ to }: { to: (id: string) => string }) => {
  const { id } = useParams();
  return <Navigate to={to(id!)} replace />;
};

export function App() {
  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/verify" element={<OtpPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route
          element={
            <RequireSession>
              <AppShell />
            </RequireSession>
          }
        >
          <Route index element={<Home />} />
          <Route path="onboarding" element={<Shared.Onboarding />} />
          <Route path="orders" element={<Shared.Orders />} />
          <Route path="orders/:id" element={<Shared.Order />} />
          <Route path="approvals" element={<Shared.Orders approvals />} />
          <Route path="rfqs" element={<Shared.Rfqs />} />
          <Route path="rfqs/new" element={<ForType type="BUYER"><Buyer.NewRfq /></ForType>} />
          <Route path="rfqs/:id" element={<Shared.Rfq />} />
          <Route path="buyer/rfqs/:id" element={<RedirectParam to={(id) => `/rfqs/${id}`} />} />
          <Route path="supplier/rfqs/:id" element={<RedirectParam to={(id) => `/rfqs/${id}`} />} />
          <Route path="invoices" element={<Shared.Invoices />} />
          <Route path="invoices/:id" element={<Shared.Invoice />} />
          <Route path="disputes" element={<Shared.Disputes />} />
          <Route path="disputes/:id" element={<Shared.Dispute />} />
          <Route path="support" element={<Shared.Support />} />
          <Route path="support/:id" element={<Shared.Ticket />} />
          <Route path="admin/support/:id" element={<RedirectParam to={(id) => `/support/${id}`} />} />
          <Route path="settings" element={<Shared.Settings />} />
          <Route path="company" element={<Shared.Company />} />
          <Route path="notifications" element={<Shared.Notifications />} />

          <Route path="catalog" element={<ForType type="BUYER"><Buyer.Catalog /></ForType>} />
          <Route path="catalog/:id" element={<ForType type="BUYER"><Buyer.Product /></ForType>} />
          <Route path="suppliers" element={<ForType type="BUYER"><Buyer.Suppliers /></ForType>} />
          <Route path="suppliers/:id" element={<ForType type="BUYER"><Buyer.Supplier /></ForType>} />
          <Route path="cart" element={<ForType type="BUYER"><Buyer.Cart /></ForType>} />

          <Route path="supplier/products" element={<ForType type="SUPPLIER"><SupplierPages.Products /></ForType>} />
          <Route path="supplier/products/new" element={<ForType type="SUPPLIER"><SupplierPages.ProductEditor /></ForType>} />
          <Route path="supplier/products/:id" element={<ForType type="SUPPLIER"><SupplierPages.ProductEditor /></ForType>} />
          <Route path="supplier/finance" element={<ForType type="SUPPLIER"><SupplierPages.Finance /></ForType>} />

          <Route path="admin/kyc" element={<ForType type="PLATFORM"><Admin.Kyc /></ForType>} />
          <Route path="admin/kyc/:id" element={<ForType type="PLATFORM"><Admin.KycDetail /></ForType>} />
          <Route path="admin/organizations" element={<ForType type="PLATFORM"><Admin.Organizations /></ForType>} />
          <Route path="admin/organizations/:id" element={<ForType type="PLATFORM"><Admin.Organization /></ForType>} />
          <Route path="admin/moderation" element={<ForType type="PLATFORM"><Admin.Moderation /></ForType>} />
          <Route path="admin/categories" element={<ForType type="PLATFORM"><Admin.Categories /></ForType>} />
          <Route path="admin/users" element={<ForType type="PLATFORM"><Admin.Users /></ForType>} />
          <Route path="admin/payments" element={<ForType type="PLATFORM"><Admin.Payments /></ForType>} />
          <Route path="admin/settlements" element={<ForType type="PLATFORM"><Admin.Settlements /></ForType>} />
          <Route path="admin/commissions" element={<ForType type="PLATFORM"><Admin.Commissions /></ForType>} />
          <Route path="admin/content" element={<ForType type="PLATFORM"><Admin.Content /></ForType>} />
          <Route path="admin/audit" element={<ForType type="PLATFORM"><Admin.Audit /></ForType>} />
          <Route path="admin/configuration" element={<ForType type="PLATFORM"><Admin.Configuration /></ForType>} />
          <Route path="admin/integrations" element={<ForType type="PLATFORM"><Admin.Integrations /></ForType>} />
          <Route path="*" element={<div className="py-20 text-center text-ink-3">This page does not exist.</div>} />
        </Route>
      </Routes>
    </Suspense>
  );
}
